import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useGetPricingPlanTier } from 'src/features/pricingPlan/api/tiers/useGetPricingPlanTier';
import { useGetPricingPlan } from 'src/features/pricingPlan/api/useGetPricingPlan';
import TierForm from 'src/features/pricingPlan/components/form/tiers/TierForm';
import { pricingPlanTierFormSchema, PricingPlanTierFormSchemaValues } from 'src/features/pricingPlan/utils/formSchema';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PRICING_PLAN_STATUS, PricingPlanTier } from 'src/features/pricingPlan/utils/types';
import { useParking } from 'src/providers/ParkingProvider';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';

interface TierFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedTier?: PricingPlanTier;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: PricingPlanTierFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function TierFormModal<TData>({
    selectedTier,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: TierFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { planId = '' } = useParams();
    const { parkingId } = useParking();

    const { data: tierData } = useGetPricingPlanTier({
        planId,
        parkingId,
        tierId: selectedTier?.id ?? '',
        config: { enabled: !!planId && !!selectedTier?.id && isModalOpen && !!parkingId },
    });

    const { data: pricingPlanData } = useGetPricingPlan({
        parkingId,
        planId,
        config: { enabled: !!planId },
    });

    const onError = useOnError();

    const defaultValues = React.useMemo(() => {
        return {
            start_minute: tierData?.start_minute ?? undefined,
            end_minute: tierData?.end_minute ?? undefined,
            rate_with_tax: tierData?.rate_with_tax ?? undefined,
            tick_type: tierData?.tick_type ?? undefined,
            tick_minutes: tierData?.tick_minutes ?? undefined,
        };
    }, [tierData]);

    const form = useForm<PricingPlanTierFormSchemaValues>({
        resolver: zodResolver(pricingPlanTierFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [form, defaultValues]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: PricingPlanTierFormSchemaValues) =>
            mutate(
                values,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            ),
        [mutate, onError, form.setError],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <span className="inline-block">
                                <DialogTrigger asChild>
                                    <Button
                                        className={className}
                                        disabled={pricingPlanData?.status === PRICING_PLAN_STATUS.PUBLISHED}
                                    >
                                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                                        {title}
                                    </Button>
                                </DialogTrigger>
                            </span>
                        </TooltipTrigger>
                        {pricingPlanData?.status === PRICING_PLAN_STATUS.PUBLISHED && (
                            <TooltipContent side="top" className="max-w-56">
                                <FormattedMessage {...pricingPlanMessages.publishedPricingPlanError} />
                            </TooltipContent>
                        )}
                    </Tooltip>
                </TooltipProvider>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <TierForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedTier && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default TierFormModal;
